import { useEffect, useState } from "react";
import api, { getViewerId } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Radio, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";
import { TipOverlay } from "@/components/Tips";
import { GiftOverlay } from "@/components/Gifts";
import TopSupporters from "@/components/TopSupporters";
import LiveKitStream from "@/components/LiveKitStream";
import LiveComments from "@/components/LiveComments";
import CopyrightNotice from "@/components/CopyrightNotice";

const STUDIO_BG = "https://images.unsplash.com/photo-1633933757597-2d09c360c2ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxyZWNvcmRpbmclMjBzdHVkaW8lMjBuZW9ufGVufDB8fHx8MTc3ODk4MjAzNHww&ixlib=rb-4.1.0&q=85";

export default function LivesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  // True while auto-joining a ?start=<id> live — used to skip straight to
  // the fullscreen camera view instead of flashing the lives grid/join list
  // underneath while the list + join requests were previously awaited in
  // sequence.
  const [joining, setJoining] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/lives"); setLives(data); return data; }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Auto-open the LiveRoom if ?start=<id> is present in the URL — fired in
    // parallel with load(), not after it, so the artist goes straight to the
    // camera instead of seeing the (loading) lives grid first.
    const params = new URLSearchParams(window.location.search);
    const startId = params.get("start");
    if (startId) {
      setJoining(true);
      api.post(`/lives/${startId}/join`, { viewer_id: getViewerId(user?.id) })
        .then(({ data }) => setActive(data))
        .catch(() => toast.error(tr("Could not join")))
        .finally(() => setJoining(false));
    }
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async (l) => {
    try {
      const { data } = await api.post(`/lives/${l.id}/join`, { viewer_id: getViewerId(user?.id) });
      setActive(data);
    } catch { toast.error(tr("Could not join")); }
  };

  if (joining && !active) {
    return (
      <div className="fixed inset-0 z-[60] bg-black grid place-items-center" data-testid="live-auto-join">
        <Loader2 className="w-10 h-10 text-[#FF0050] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <header className="anim-in mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold">{t.lives.title}</p>
        <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{t.lives.title}</h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.lives.sub}</p>
      </header>

      {renderLivesList(loading, lives, t, join)}

      <div className="mt-10 pt-6 border-t border-white/5">
        <CopyrightNotice />
      </div>

      {active && <LiveRoom live={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function renderLivesList(loading, lives, t, join) {
  if (loading) return <p className="text-center text-zinc-500 py-12">{t.common.loading}</p>;
  if (lives.length === 0) return (
    <div className="card-flat p-12 text-center">
      <Radio className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
      <p className="text-zinc-400">{t.lives.empty}</p>
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="lives-grid">
      {lives.map((l) => (
        <div key={l.id} className="card-interactive overflow-hidden" data-testid={`live-${l.id}`}>
          <div className="relative aspect-video">
            <img src={STUDIO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
            <span className="absolute top-3 left-3 chip bg-[#FF0050] text-white inline-flex items-center gap-1 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
            </span>
            <span className="absolute top-3 right-3 chip bg-black/60 text-white inline-flex items-center gap-1 border border-white/10">
              <Eye className="w-3 h-3" /> {l.viewers}
            </span>
          </div>
          <div className="p-4">
            <p className="font-semibold text-white truncate">{l.title}</p>
            <p className="text-xs text-zinc-400 truncate">{l.artist_name}</p>
            <button
              onClick={() => join(l)}
              className="mt-3 btn-live w-full inline-flex items-center justify-center gap-2"
              data-testid={`join-live-${l.id}`}
            >
              <Radio className="w-4 h-4" /> {t.lives.join}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LiveRoom({ live, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [viewers, setViewers] = useState(live.viewers);

  const isOwner = !!user && user.id === live.artist_id;

  // Real, server-computed viewer count (was a per-client random local
  // counter before — that's why the artist and a fan watching the same live
  // at the same time saw completely different numbers). Every connected
  // client — owner included — pings presence and reads back the same count.
  useEffect(() => {
    const viewerId = getViewerId(user?.id);
    const beat = () => {
      api.post(`/lives/${live.id}/viewer-heartbeat`, { viewer_id: viewerId })
        .then(({ data }) => setViewers(data.viewers))
        .catch((err) => {
          if (err.response?.status === 403) {
            toast.error("Vous avez été banni de ce live");
            onClose();
          }
        });
    };
    beat();
    const i = setInterval(beat, 15000);
    return () => {
      clearInterval(i);
      api.post(`/lives/${live.id}/leave`, { viewer_id: viewerId }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.id]);

  // For the artist, the × must fully end the live in one click — not just
  // dismiss the overlay while it keeps running server-side, forcing a trip
  // back to the profile's "Stop Live" button. Fans just leave the view.
  const handleClose = () => {
    if (isOwner) api.post(`/lives/${live.id}/end`).catch(() => {});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black anim-in" data-testid="live-room">
      <div className="relative w-full h-full">
        {/* Background poster while connecting */}
        <img src={STUDIO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        {/* Real WebRTC (falls back to placeholder if not configured) */}
        <LiveKitStream liveId={live.id} isOwner={isOwner} />

        <button onClick={handleClose} className="absolute top-4 right-4 z-30 w-9 h-9 grid place-items-center rounded-full bg-black/60 text-white text-lg border border-white/10" data-testid="live-close">×</button>
        <span className="absolute top-4 left-4 z-20 chip bg-[#FF0050] text-white inline-flex items-center gap-1 animate-pulse">
          <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
        </span>
        <span className="absolute top-4 left-24 z-20 chip bg-black/60 text-white border border-white/10 inline-flex items-center gap-1">
          <Eye className="w-3 h-3" /> {viewers} {t.lives.viewers}
        </span>

        {/* Tip overlay & button & Top supporters & Gifts — the artist can't
            tip/gift themselves, so these switch to a discreet notification
            for isOwner instead of the big animated overlay fans see. */}
        <TopSupporters liveId={live.id} />
        <TipOverlay liveId={live.id} isOwner={isOwner} />
        <GiftOverlay liveId={live.id} isOwner={isOwner} />
        {/* Tip / gift buttons are hidden until Notes and tips are wired to a real payment provider. */}

        {/* Live chat: comment feed + input, owner moderation, viewers list */}
        <LiveComments liveId={live.id} isOwner={isOwner} />

        {/* Title / artist overlay, bottom-left like TikTok/Instagram/Facebook Live */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-4 pb-6 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none">
          <p className="text-xl font-black font-display text-white">{live.title}</p>
          <p className="text-sm text-zinc-300">by {live.artist_name}</p>
          <CopyrightNotice className="mt-2 pointer-events-auto" />
        </div>
      </div>
    </div>
  );
}
